import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Textarea } from '@artosera/ui';
import type { GesteResolution } from '@artosera/shared';
import {
  reserveApi,
  useCampagne,
  useMutationReserve,
  type LigneInventaire,
} from '../../api/reserve.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { identiteCourante, PorteeObsolete } from '../../auth/identite.js';
import { fileHorsLigne } from '../../horsLigne/fileHorsLigne.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { Scanner, type DecoderQr } from './Scanner.js';
import styles from './InventairePage.module.css';

export function CampagneInventairePage({ decoderQr }: { decoderQr?: DecoderQr }) {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie, compte } = useShellContext();
  const { id = '' } = useParams();
  const fiche = useCampagne(galerieSlug, id);
  const [enFile, setEnFile] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [refus, setRefus] = useState<string[]>([]);
  const [resolution, setResolution] = useState<{
    ligne: LigneInventaire;
    geste: GesteResolution;
  } | null>(null);
  const [note, setNote] = useState('');
  const [cloture, setCloture] = useState(false);
  const ecrit = galerieALaction(galerie, 'inventaire:write');
  const resoudre = useMutationReserve(
    galerieSlug,
    (corps: { cle: string; geste: GesteResolution; note: string; revision: number }, appel) =>
      reserveApi.resoudreEcart(galerieSlug, id, corps, appel),
  );
  const clore = useMutationReserve(galerieSlug, (revision: number, appel) =>
    reserveApi.clore(galerieSlug, id, revision, appel),
  );
  const rapport = useMutationReserve(galerieSlug, (_: void, appel) =>
    reserveApi.rapport(galerieSlug, id, appel),
  );
  const { refetch } = fiche;
  const rejouer = useCallback(async () => {
    const portee = { compteId: compte.id, galerie: galerieSlug, campagneId: id };
    if (navigator.onLine && ecrit) {
      const messages = await fileHorsLigne.rejouer(portee, (scan) => {
        const identite = identiteCourante();
        if (identite?.compteId !== compte.id || identite.galerieSlug !== galerieSlug)
          throw new PorteeObsolete();
        return dansLaPorteeCourante((appel) => reserveApi.scans(galerieSlug, id, [scan], appel));
      });
      if (messages.length) setRefus((avant) => [...avant, ...messages]);
      void refetch();
    }
    setEnFile((await fileHorsLigne.lister(portee)).length);
  }, [compte.id, galerieSlug, id, ecrit, refetch]);
  useEffect(() => {
    queueMicrotask(() => void rejouer());
    const retour = () => void rejouer();
    window.addEventListener('online', retour);
    return () => window.removeEventListener('online', retour);
  }, [rejouer]);
  async function scanner(code: string) {
    setEnFile((avant) => (avant ?? 0) + 1);
    await fileHorsLigne.ajouter(
      { compteId: compte.id, galerie: galerieSlug, campagneId: id },
      code,
    );
    setEnFile(
      (await fileHorsLigne.lister({ compteId: compte.id, galerie: galerieSlug, campagneId: id }))
        .length,
    );
    setMessage(t('regie:inventaire.scanRecu'));
    navigator.vibrate?.(50);
    await rejouer();
  }
  const erreur = resoudre.error ?? clore.error ?? rapport.error;
  const c = fiche.data?.campagne;
  const resultat = fiche.data?.resultat;
  if (!c || !resultat)
    return (
      <p role={fiche.isError ? 'alert' : 'status'}>
        {t(fiche.isError ? 'commun:erreurReseau' : 'commun:chargement')}
      </p>
    );
  return (
    <section className={styles.page} data-ui="page">
      <Link className={styles.lien} to={`/${galerieSlug}/inventaire?onglet=campagnes`}>
        {t('regie:inventaire.campagnes')}
      </Link>
      <header className={styles.entete} data-ui="page-heading">
        <h1>{c.numero}</h1>
        <span>{t(`regie:inventaire.etatsCampagne.${c.etat}`)}</span>
      </header>
      <div className={styles.compteurs}>
        {(['presentes', 'absentes', 'inattendues', 'inconnus'] as const).map((cle) => (
          <p key={cle}>
            {t(`regie:inventaire.${cle}`)} : <strong>{resultat[cle].length}</strong>
          </p>
        ))}
      </div>
      <p role="status">
        {message}{' '}
        {enFile === null ? t('commun:chargement') : t('regie:inventaire.enFile', { count: enFile })}
      </p>
      {refus.length > 0 && (
        <div role="alert">
          <p>{t('regie:inventaire.scansRefuses')}</p>
          {refus.map((texte, i) => (
            <p key={i}>{texte}</p>
          ))}
        </div>
      )}
      {c.etat === 'ouverte' && ecrit && (
        <Scanner onScan={scanner} decoderQr={decoderQr} desactive={clore.isPending} />
      )}
      {(['absentes', 'inattendues'] as const).map((cle) => (
        <section className={styles.panneau} key={cle}>
          <h2>{t(`regie:inventaire.${cle}`)}</h2>
          <ul className={styles.arbre}>
            {resultat[cle].map((ligne) => (
              <li key={ligne.cle} className={styles.ligne}>
                <div>
                  <strong>{ligne.titre}</strong>
                  <p>
                    {ligne.numero} · {ligne.lieuAttendu}
                  </p>
                  {ligne.resolution && (
                    <p>
                      {t(`regie:inventaire.gestes.${ligne.resolution.geste}`)} :{' '}
                      {ligne.resolution.note}
                    </p>
                  )}
                </div>
                {c.etat === 'ouverte' && ecrit && (
                  <div className={styles.actions}>
                    {(['deplacer_ici', 'introuvable', 'ignorer'] as const)
                      .filter((geste) => geste !== 'introuvable' || ligne.cible.type !== 'caisse')
                      .map((geste) => (
                        <Button
                          key={geste}
                          variant="secondary"
                          disabled={enFile !== 0 || resoudre.isPending}
                          onClick={() => {
                            setResolution({ ligne, geste });
                            setNote('');
                          }}
                        >
                          {t(`regie:inventaire.gestes.${geste}`)}
                        </Button>
                      ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {resultat.inconnus.length > 0 && (
        <section className={styles.panneau}>
          <h2>{t('regie:inventaire.inconnus')}</h2>
          <ul>
            {resultat.inconnus.map((scan) => (
              <li key={scan.id}>{scan.code}</li>
            ))}
          </ul>
        </section>
      )}
      <section className={styles.panneau}>
        <h2>{t('regie:inventaire.scanneurs')}</h2>
        {resultat.scanneurs.map((s) => (
          <p key={s.auteurId}>
            {s.auteurId ?? '—'} : {s.nombre}
          </p>
        ))}
      </section>
      {c.etat === 'ouverte' && galerieALaction(galerie, 'inventaire:manage') && (
        <Button disabled={enFile !== 0 || clore.isPending} onClick={() => setCloture(true)}>
          {t('regie:inventaire.clore')}
        </Button>
      )}
      {c.rapportDocumentId && (
        <Button
          onClick={() => rapport.mutate(undefined, { onSuccess: (r) => ouvrirUrlSignee(r.url) })}
        >
          {t('regie:inventaire.rapport')}
        </Button>
      )}
      {erreur && (
        <p role="alert">{estApiError(erreur) ? erreur.message : t('commun:erreurReseau')}</p>
      )}
      <Dialog
        open={resolution !== null}
        onOpenChange={(v) => {
          if (!v) setResolution(null);
        }}
        title={resolution ? t(`regie:inventaire.gestes.${resolution.geste}`) : ''}
        closeLabel={t('commun:actions.fermer')}
      >
        <div className={styles.formulaire}>
          <p>{resolution?.ligne.titre}</p>
          <Field htmlFor="resolution-note" label={t('regie:inventaire.note')}>
            <Textarea
              id="resolution-note"
              maxLength={5000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required={resolution?.geste === 'introuvable'}
            />
          </Field>
          <Button
            disabled={
              !resolution ||
              (resolution.geste === 'introuvable' && !note.trim()) ||
              resoudre.isPending
            }
            onClick={() => {
              if (resolution)
                resoudre.mutate(
                  {
                    cle: resolution.ligne.cle,
                    geste: resolution.geste,
                    note,
                    revision: c.revision,
                  },
                  { onSuccess: () => setResolution(null) },
                );
            }}
          >
            {t('regie:inventaire.confirmer')}
          </Button>
          {resoudre.error && (
            <p role="alert">
              {estApiError(resoudre.error) ? resoudre.error.message : t('commun:erreurReseau')}
            </p>
          )}
        </div>
      </Dialog>
      <Dialog
        open={cloture}
        onOpenChange={setCloture}
        title={t('regie:inventaire.clore')}
        description={t('regie:inventaire.cloreConfirmation')}
        closeLabel={t('commun:actions.fermer')}
      >
        <Button
          disabled={enFile !== 0 || clore.isPending}
          onClick={() => clore.mutate(c.revision, { onSuccess: () => setCloture(false) })}
        >
          {t('regie:inventaire.confirmer')}
        </Button>
        {clore.error && (
          <p role="alert">
            {estApiError(clore.error) ? clore.error.message : t('commun:erreurReseau')}
          </p>
        )}
      </Dialog>
    </section>
  );
}
